import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

/**
 * Design-system checkbox. Custom appearance-none box with a CSS-drawn check on
 * :checked (brand fill). Single source for every checkbox in the app + the
 * design-system gallery — change it here and it updates across the board.
 *
 * Check offsets measured 2026-09-09 at 5x against four alternatives: the
 * 6x10 rotated glyph at left 3px sat one pixel left of the 16px box's centre
 * (Emily: "checkbox setup ... looks off"); left 4px centres it. The 14px
 * compact variant keeps its own offsets.
 */
// The mixed state (a select-all over a partial selection sets the DOM
// `indeterminate` property) draws an 8x2 dash the same way the tick is drawn.
// Until 2026-09-09 only `checked:` had a glyph, so a partial selection rendered
// an EMPTY box while aria-checked said "mixed" (measured in the add-scan modal
// A/B on the mini). Tailwind's `indeterminate:` variant is :indeterminate.
export const CHECKBOX_CLASS =
  "block w-4 h-4 rounded appearance-none border border-gray-200 checked:bg-primary-500 checked:border-primary-500 relative checked:after:content-[''] checked:after:absolute checked:after:left-[4px] checked:after:top-[1px] checked:after:w-[6px] checked:after:h-[10px] checked:after:border-white checked:after:border-r-2 checked:after:border-b-2 checked:after:rotate-45 indeterminate:bg-primary-500 indeterminate:border-primary-500 indeterminate:after:content-[''] indeterminate:after:absolute indeterminate:after:left-[3px] indeterminate:after:top-[6px] indeterminate:after:w-[8px] indeterminate:after:h-[2px] indeterminate:after:bg-white cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed";

/** Compact variant (dense table rows): 14px box with a proportionally smaller check and dash. */
const CHECKBOX_SM =
  'w-3.5 h-3.5 checked:after:left-[3px] checked:after:top-[0px] checked:after:w-[5px] checked:after:h-[9px] indeterminate:after:left-[2px] indeterminate:after:top-[5px] indeterminate:after:w-[8px] indeterminate:after:h-[2px]';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  size?: 'sm' | 'md';
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(({ className, size = 'md', ...props }, ref) => (
  <input ref={ref} type="checkbox" className={cn(CHECKBOX_CLASS, size === 'sm' && CHECKBOX_SM, className)} {...props} />
));
Checkbox.displayName = 'Checkbox';
